<template>
	<nav class="app-pagination" aria-label="Pagination navigation">
		<button type="button" class="app-pagination__nav" :disabled="isInFirstPage"
			@click="onClickFirstPage" title="First page" aria-label="First page">
			<i class="fas fa-angle-double-left"></i>
		</button>
		<button type="button" class="app-pagination__nav" :disabled="isInFirstPage"
			@click="onClickPreviousPage" title="Previous page" aria-label="Previous page">
			<i class="fas fa-angle-left"></i>
		</button>

		<div class="app-pagination__pages">
			<button v-for="(page, key) in pages" :key="key" type="button"
				class="app-pagination__page" :class="{ 'app-pagination__page--active': isPageActive(page.name) }"
				@click="onClickPage(page.name)" :aria-label="'Page ' + page.name"
				:aria-current="isPageActive(page.name) ? 'page' : null">
				{{ page.name }}
			</button>
		</div>

		<button type="button" class="app-pagination__nav" :disabled="isInLastPage"
			@click="onClickNextPage" title="Next page" aria-label="Next page">
			<i class="fas fa-angle-right"></i>
		</button>
		<button type="button" class="app-pagination__nav" :disabled="isInLastPage"
			@click="onClickLastPage" title="Last page" aria-label="Last page">
			<i class="fas fa-angle-double-right"></i>
		</button>
	</nav>
</template>

<script>
export default {
	name: "Pagination",
	props: {
		maxPage: {
			type: Number,
			required: true,
		},
		totalPages: {
			type: Number,
			required: true,
		},
		currentPage: {
			type: Number,
			required: true,
		},
	},

	computed: {
		startPage() {
			if (this.totalPages <= 0) {
				return 1;
			}
			if (this.currentPage === 1) {
				return 1;
			}
			if (this.currentPage === this.totalPages) {
				return Math.max(1, this.totalPages - this.maxPage + 1);
			}
			return this.currentPage - 1;
		},
		endPage() {
			if (this.totalPages <= 0) {
				return 1;
			}
			return Math.min(this.startPage + this.maxPage - 1, this.totalPages);
		},
		pages() {
			const range = [];
			if (this.totalPages <= 0) {
				return [{ name: 1, isDisabled: true }];
			}
			for (let i = this.startPage; i <= this.endPage; i += 1) {
				range.push({
					name: i,
					isDisabled: i === this.currentPage,
				});
			}
			return range;
		},
		isInFirstPage() {
			return this.currentPage <= 1 || this.totalPages <= 0;
		},
		isInLastPage() {
			return this.currentPage >= this.totalPages || this.totalPages <= 0;
		},
	},

	methods: {
		onClickFirstPage() {
			if (!this.isInFirstPage) {
				this.$emit("pagechanged", 1);
			}
		},
		onClickPreviousPage() {
			if (!this.isInFirstPage) {
				this.$emit("pagechanged", this.currentPage - 1);
			}
		},
		onClickPage(page) {
			this.$emit("pagechanged", page);
		},
		onClickNextPage() {
			if (!this.isInLastPage) {
				this.$emit("pagechanged", this.currentPage + 1);
			}
		},
		onClickLastPage() {
			if (!this.isInLastPage) {
				this.$emit("pagechanged", this.totalPages);
			}
		},
		isPageActive(page) {
			return this.currentPage === page;
		},
	},
};
</script>

<style scoped>
.app-pagination {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	flex-wrap: wrap;
}

.app-pagination__nav,
.app-pagination__page {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 32px;
	height: 32px;
	padding: 0 8px;
	font-size: 0.8rem;
	font-weight: 500;
	line-height: 1;
	color: #495057;
	background: #fff;
	border: 1px solid #dee2e6;
	border-radius: 6px;
	cursor: pointer;
	transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.app-pagination__nav {
	padding: 0 10px;
}

.app-pagination__nav:hover:not(:disabled),
.app-pagination__page:hover:not(.app-pagination__page--active) {
	background: #f1f3f5;
	border-color: #adb5bd;
}

.app-pagination__nav:disabled {
	opacity: 0.45;
	cursor: not-allowed;
	background: #f8f9fa;
}

.app-pagination__pages {
	display: inline-flex;
	align-items: center;
	gap: 4px;
}

.app-pagination__page {
	min-width: 34px;
}

.app-pagination__page--active {
	background: #007bff;
	border-color: #007bff;
	color: #fff;
}

.app-pagination__page--active:hover {
	background: #0069d9;
	border-color: #0062cc;
	color: #fff;
}
</style>
